/**
 * The reminder vocabulary, with its Hebrew labels.
 *
 * Lives here rather than beside the rule engine for the same reason the status
 * vocabulary does: the rule builder, the sweep, the API's validation and any
 * export all have to name a module or an audience identically. A rule reading
 * "מנהל/ת חשבונות" on one screen and "מנהלת חשבונות" on another looks like two
 * different things to the person deciding whether to switch it off.
 *
 * `@onecpa/domain` imports these types and adds the rules that operate on them.
 */

/** How often a rule repeats once it has fired. */
export type ReminderFrequency =
  | "one_time"
  | "daily"
  | "weekly"
  | "bi_weekly"
  | "monthly"
  | "bi_monthly"
  | "semi_annual"
  | "annual"
  | "custom";

export const REMINDER_FREQUENCY_LABELS: Record<ReminderFrequency, string> = {
  one_time: "חד פעמי",
  daily: "יומי",
  weekly: "שבועי",
  bi_weekly: "דו-שבועי",
  monthly: "חודשי",
  bi_monthly: "דו-חודשי",
  semi_annual: "חצי שנתי",
  annual: "שנתי",
  custom: "כל X ימים",
};

/** Which part of the product a rule watches. */
export type ReminderModule =
  | "monthly_reporting"
  | "annual_report"
  | "wealth_declaration"
  | "documents"
  | "billing"
  | "general";

export const REMINDER_MODULE_LABELS: Record<ReminderModule, string> = {
  monthly_reporting: "דיווחים חודשיים",
  annual_report: "דוחות שנתיים",
  wealth_declaration: "הצהרות הון",
  documents: "מסמכים",
  billing: "גבייה",
  general: "כללי",
};

/**
 * Who the message is for.
 *
 * Flattened from the legacy's five values: `client_manager_user` was a role that
 * never shipped, and the three office audiences map onto the roles this product
 * actually has.
 */
export type ReminderAudience = "client" | "bookkeeper" | "manager" | "owner";

export const REMINDER_AUDIENCE_LABELS: Record<ReminderAudience, string> = {
  client: "הלקוח",
  bookkeeper: "מנהל/ת החשבונות",
  manager: "מנהל/ת המשרד",
  owner: "בעל/ת המשרד",
};

/**
 * What makes a rule fire.
 *
 * Two, deliberately. The legacy had five, of which `manual`, `by_date` and
 * `by_item_feedback` were all "something reached a state" expressed three
 * different ways — and `by_inactivity` is what the escalation ladder already
 * does properly.
 */
export type ReminderTrigger =
  /** The watched entity is sitting in `statusTrigger`, N days past its due date. */
  | "by_status"
  /** Repeats every `intervalDays` for as long as the condition holds. */
  | "by_interval";

export const REMINDER_TRIGGER_LABELS: Record<ReminderTrigger, string> = {
  by_status: "לפי סטטוס",
  by_interval: "לפי מרווח ימים",
};

/**
 * The statuses a rule can watch, per module.
 *
 * Derived from each module's own status vocabulary rather than being free text,
 * so a rule cannot be saved against a status that no entity will ever hold —
 * which in the legacy produced rules that silently never fired.
 */
export const REMINDER_STATUS_OPTIONS: Record<ReminderModule, { value: string; label: string }[]> = {
  monthly_reporting: [
    { value: "awaiting_client", label: "ממתין לחומר" },
    { value: "late", label: "באיחור" },
    { value: "penalty_risk", label: "סיכון לקנס" },
    { value: "penalty_incurred", label: "קנס בפועל" },
  ],
  annual_report: [
    { value: "not_started", label: "לא התחיל" },
    { value: "in_progress", label: "בטיפול" },
    { value: "requires_completion", label: "דורש השלמה" },
  ],
  wealth_declaration: [
    { value: "not_started", label: "לא התחיל" },
    { value: "in_progress", label: "בטיפול" },
    { value: "requires_completion", label: "דורש השלמה" },
  ],
  documents: [],
  billing: [
    { value: "sent", label: "נשלח" },
    { value: "overdue", label: "בפיגור" },
  ],
  general: [],
};
